/**
 * Wire-контракт товаров 1С (B2a, `ProductDtos.cs`) и проверка на границе
 * (D-056). Неожиданный ответ — ошибка `invalid-response`, а не пустой экран.
 * `productId` в ответах службы 1С — ссылка товара В ЭТОЙ базе (native identity),
 * не общий ProductId Platform.
 */
import { bool, fail, isRecord, list, num, onecEnvelopeDecoder, optionalText, text } from '../../../shared/api/onecEnvelope'
import type {
  BarcodeMutationResult,
  CatalogRefreshResult,
  CreateProductResult,
  ProductBarcode,
  ProductCard,
  ProductCandidate,
} from '../model/types'

export const ONEC_PRODUCT_SCHEMAS = {
  create: 'onec.business.Catalog_CreateProduct.v1',
  update: 'onec.business.Catalog_UpdateProduct.v1',
  delete: 'onec.business.Catalog_DeleteProduct.v1',
  addBarcode: 'onec.business.Catalog_AddProductBarcode.v1',
  deleteBarcode: 'onec.business.Catalog_DeleteProductBarcode.v1',
  setMainBarcode: 'onec.business.Catalog_SetMainBarcode.v1',
  triggerSync: 'onec.business.Catalog_TriggerSync.v1',
} as const

/** Итог `Catalog_TriggerSync` (`TriggerSyncResultDto`): счётчики своего зеркала, не создание товаров в 1С. */
export function decodeTriggerSync(raw: unknown): CatalogRefreshResult {
  const item = record(raw, 'итог обновления каталога')
  return {
    refreshKind: text(item.kind, 'kind'),
    read: num(item.read, 'read'),
    created: num(item.created, 'created'),
    updated: num(item.updated, 'updated'),
    skipped: num(item.skipped, 'skipped'),
  }
}

/**
 * `GET …/sync/capabilities` (native, `OneCSyncCapabilitiesDto`): rid и ревизия — на верхнем уровне, как у конверта;
 * нужны ревизия для команды и виды обновления, которые база умеет.
 */
export const decodeSyncCapabilities = (resourceId: string) => (raw: unknown): { configurationVersion: string; refreshKinds: string[] } => {
  const envelope = onecEnvelopeDecoder(resourceId, () => null)(raw)
  const item = record(raw, 'возможности синхронизации')
  return {
    configurationVersion: envelope.configurationVersion,
    refreshKinds: list(item.refreshKinds, 'refreshKinds', (kind, index) => text(kind, `refreshKinds[${index}]`)),
  }
}

function record(raw: unknown, what: string): Record<string, unknown> {
  return isRecord(raw) ? raw : fail(`${what} — не объект`)
}

export function decodeCandidate(raw: unknown, index = 0): ProductCandidate {
  const item = record(raw, `товар[${index}]`)
  const refKey = optionalText(item.oneCRefKey, 'oneCRefKey') ?? text(item.productId, 'productId')
  return {
    refKey,
    name: text(item.name, 'name'),
    sku: optionalText(item.sku, 'sku'),
    barcode: optionalText(item.barcode, 'barcode'),
    strategy: optionalText(item.strategy, 'strategy'),
  }
}

export const decodeCandidates = (raw: unknown): ProductCandidate[] => list(raw, 'data', decodeCandidate)

export function decodeCard(raw: unknown): ProductCard {
  const item = record(raw, 'карточка')
  return {
    refKey: text(item.refKey, 'refKey'),
    name: text(item.name, 'name'),
    article: optionalText(item.article, 'article'),
    primaryBarcode: optionalText(item.primaryBarcode, 'primaryBarcode'),
    // Служба до версии со ставкой НДС полей не присылает: тогда «нет данных», а не «не указана».
    vatRateCode: optionalText(item.vatRateCode, 'vatRateCode'),
    vatState: optionalText(item.vatState, 'vatState') ?? 'unavailable',
  }
}

export const decodeBarcodes = (raw: unknown): ProductBarcode[] =>
  list(raw, 'data', (entry, index) => {
    const item = record(entry, `штрихкод[${index}]`)
    return { barcode: text(item.barcode, 'barcode'), isMain: bool(item.isMain, 'isMain') }
  })

export function decodeCreateResult(raw: unknown): CreateProductResult {
  const item = record(raw, 'итог создания')
  const outcome = text(item.outcome, 'outcome')
  if (outcome !== 'Created' && outcome !== 'AlreadyExists') fail(`outcome — неизвестное значение ${outcome}`)
  return { refKey: text(item.refKey, 'refKey'), outcome, barcodeWarning: optionalText(item.barcodeWarning, 'barcodeWarning') }
}

export function decodeUpdateResult(raw: unknown): { changed: boolean } {
  return { changed: bool(record(raw, 'итог правки').changed, 'changed') }
}

export function decodeDeleteResult(raw: unknown): { deleted: boolean } {
  return { deleted: bool(record(raw, 'итог пометки').deleted, 'deleted') }
}

export function decodeBarcodeMutation(raw: unknown): BarcodeMutationResult {
  const item = record(raw, 'итог штрихкода')
  return { outcome: text(item.outcome, 'outcome'), message: optionalText(item.message, 'message') }
}
